/**
 * GLOBAL CALENDAR (F1): merges meetings (with recurrence materialized in
 * window + exceptions) and rehearsals into event list filterable by
 * church / venue / group / activity type / public-private / date
 * range. All pure: visibility was already resolved by sync (local SQLite
 * only contains authorized data).
 */

import {
  type ExceptionLike,
  materializeOccurrences,
  type MeetingLike,
  type OccurrenceStatus,
  type OccurrenceWindow,
  parseInstant,
} from "../recurrence";

export type CalendarSource = "meeting" | "rehearsal";

export interface CalendarEvent {
  /** Unique per event: `<sourceId>@<originalStart>`. */
  id: string;
  source: CalendarSource;
  sourceId: string;
  title: string;
  churchId: string | null;
  venueId: string | null;
  groupId: string | null;
  /** Meeting type (`service`/`cell`/...); rehearsals use `rehearsal`. */
  kind: string;
  isPublic: boolean;
  start: number;
  end: number | null;
  /** ORIGINAL instant of series (exceptions/attendance key). */
  originalStart: number;
  status: OccurrenceStatus;
  isRecurring: boolean;
}

export interface CalendarMeetingLike extends MeetingLike {
  church_id: string | null;
  venue_id?: string | null;
  group_id?: string | null;
  title: string | null;
  kind?: string | null;
  is_public?: number | boolean | null;
  deleted_at?: string | null;
}

export interface CalendarRehearsalLike {
  id: string;
  church_id: string | null;
  group_id: string | null;
  title: string | null;
  starts_at: string | null;
  ends_at?: string | null;
  deleted_at?: string | null;
}

export const REHEARSAL_KIND = "rehearsal";

/** Materializes meetings + rehearsals of window into calendar events. */
export function buildCalendarEvents(
  input: {
    meetings: readonly CalendarMeetingLike[];
    exceptions?: readonly ExceptionLike[];
    rehearsals?: readonly CalendarRehearsalLike[];
  },
  window: OccurrenceWindow,
): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const meeting of input.meetings) {
    if (meeting.deleted_at) {
      continue;
    }
    for (const occ of materializeOccurrences(meeting, window, input.exceptions ?? [])) {
      events.push({
        id: `${meeting.id}@${occ.originalStart}`,
        source: "meeting",
        sourceId: meeting.id,
        title: meeting.title ?? "",
        churchId: meeting.church_id,
        venueId: meeting.venue_id ?? null,
        groupId: meeting.group_id ?? null,
        kind: meeting.kind ?? "other",
        isPublic: Boolean(meeting.is_public),
        start: occ.start,
        end: occ.end,
        originalStart: occ.originalStart,
        status: occ.status,
        isRecurring: Boolean(meeting.rrule?.trim()),
      });
    }
  }
  for (const rehearsal of input.rehearsals ?? []) {
    if (rehearsal.deleted_at) {
      continue;
    }
    const start = parseInstant(rehearsal.starts_at);
    if (start === null || start < window.from || start > window.to) {
      continue;
    }
    events.push({
      id: `${rehearsal.id}@${start}`,
      source: "rehearsal",
      sourceId: rehearsal.id,
      title: rehearsal.title ?? "",
      churchId: rehearsal.church_id,
      venueId: null,
      groupId: rehearsal.group_id,
      kind: REHEARSAL_KIND,
      isPublic: false,
      start,
      end: parseInstant(rehearsal.ends_at),
      originalStart: start,
      status: "scheduled",
      isRecurring: false,
    });
  }
  return events.toSorted((a, b) => a.start - b.start || a.id.localeCompare(b.id));
}

export type CalendarVisibility = "all" | "public" | "private";

export interface CalendarFilter {
  churchId?: string | null;
  venueId?: string | null;
  groupId?: string | null;
  /** Allowed types (`service`, `cell`, `rehearsal`...); empty/undefined = all. */
  kinds?: readonly string[];
  visibility?: CalendarVisibility;
  /** Date range (ms UTC, inclusive) WITHIN materialized window. */
  from?: number;
  to?: number;
  /** Include canceled occurrences (default NO). */
  includeCancelled?: boolean;
}

/** Complete calendar filter. Pure and composable (each criterion is AND). */
export function filterCalendarEvents(
  events: readonly CalendarEvent[],
  filter: CalendarFilter,
): CalendarEvent[] {
  return events.filter((event) => {
    if (!filter.includeCancelled && event.status === "cancelled") {
      return false;
    }
    if (filter.churchId && event.churchId !== filter.churchId) {
      return false;
    }
    if (filter.venueId && event.venueId !== filter.venueId) {
      return false;
    }
    if (filter.groupId && event.groupId !== filter.groupId) {
      return false;
    }
    if (filter.kinds && filter.kinds.length > 0 && !filter.kinds.includes(event.kind)) {
      return false;
    }
    if (filter.visibility === "public" && !event.isPublic) {
      return false;
    }
    if (filter.visibility === "private" && event.isPublic) {
      return false;
    }
    if (filter.from !== undefined && event.start < filter.from) {
      return false;
    }
    if (filter.to !== undefined && event.start > filter.to) {
      return false;
    }
    return true;
  });
}

/** Groups events by device LOCAL day (to render schedule). */
export function groupEventsByDay(
  events: readonly CalendarEvent[],
): { day: string; events: CalendarEvent[] }[] {
  const groups = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const d = new Date(event.start);
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const list = groups.get(day);
    if (list) {
      list.push(event);
    } else {
      groups.set(day, [event]);
    }
  }
  return [...groups.entries()]
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([day, dayEvents]) => ({ day, events: dayEvents }));
}
